import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getLSectionVO,
    saveLSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    L1_PAGE,
    L2_PAGE,
    L4_PAGE,
    LAPP01_PAGE,
    LAPP02_PAGE,
    LAPP03_PAGE,
    LS_PAGE,
    L_PAGE_TITLES,
    L_SECTION_INDEX,
    L_SECTION_INDEX_LETTER,
    L_SECTION_INDEX_TITLE,
    L_SECTION_PAGES,
    L1Page,
    L4Page,
    Lapp01Page,
    Lapp02Page,
    LProgrammePage,
    LSectionPageCode,
    LSummaryPage,
} from '@/pages/components/audit-program/l-section/constants';
import LsSummaryView from '@/pages/components/audit-program/l-section/LsSummaryView';
import Lapp01SummaryView from '@/pages/components/audit-program/l-section/Lapp01SummaryView';
import Lapp02SheetView from '@/pages/components/audit-program/l-section/Lapp02SheetView';
import L4SearchView from '@/pages/components/audit-program/l-section/L4SearchView';
import JBreakdownView from '@/pages/components/audit-program/j-section/JBreakdownView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type LSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: LSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<LSectionPageCode>(['Li']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: LSectionPageCode): boolean {
    return code === 'L2' || code === 'Lapp03';
}

export default function LSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Li',
}: LSectionViewProps) {
    const [pageKey, setPageKey] = useState<LSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('L');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getLSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of L_SECTION_PAGES) {
                if (code === 'Li') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load L Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: LSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveLSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Li') as {rows?: typeof L_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : L_SECTION_INDEX;
    }, [getContent]);

    const lsContent = (getContent('Ls') ?? LS_PAGE) as LSummaryPage;
    const l2Content = (getContent('L2') ?? L2_PAGE) as LProgrammePage;
    const l1Content = (getContent('L1') ?? L1_PAGE) as L1Page;
    const l4Content = (getContent('L4') ?? L4_PAGE) as L4Page;
    const lapp01Content = (getContent('Lapp01') ?? LAPP01_PAGE) as Lapp01Page;
    const lapp02Content = (getContent('Lapp02') ?? LAPP02_PAGE) as Lapp02Page;
    const lapp03Content = (getContent('Lapp03') ?? LAPP03_PAGE) as LProgrammePage;

    const tabs = useMemo(() => L_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={L_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Li' ? (
                <SectionIndexView
                    header={header}
                    refCode="Li"
                    sectionLetter={L_SECTION_INDEX_LETTER}
                    sectionTitle={L_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as LSectionPageCode))}
                />
            ) : code === 'Ls' ? (
                <LsSummaryView
                    header={header}
                    page={lsContent}
                    values={pageData.Ls ?? {}}
                    onChange={(k, v) => handleFieldChange('Ls', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'L2' ? l2Content : lapp03Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : code === 'L1' ? (
                <JBreakdownView
                    header={header}
                    page={l1Content}
                    refCode="L1"
                    values={pageData.L1 ?? {}}
                    onChange={(k, v) => handleFieldChange('L1', k, v)}
                />
            ) : code === 'L4' ? (
                <L4SearchView
                    header={header}
                    page={l4Content}
                    values={pageData.L4 ?? {}}
                    onChange={(k, v) => handleFieldChange('L4', k, v)}
                />
            ) : code === 'Lapp01' ? (
                <Lapp01SummaryView
                    header={header}
                    page={lapp01Content}
                    values={pageData.Lapp01 ?? {}}
                    onChange={(k, v) => handleFieldChange('Lapp01', k, v)}
                />
            ) : (
                <Lapp02SheetView
                    header={header}
                    page={lapp02Content}
                    values={pageData.Lapp02 ?? {}}
                    onChange={(k, v) => handleFieldChange('Lapp02', k, v)}
                />
            )
        ) : null,
    })), [
        header, pageData, handleFieldChange, indexRows,
        lsContent, l2Content, l1Content, l4Content, lapp01Content, lapp02Content, lapp03Content,
    ]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    L Section — Trade & other payables
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as LSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
